import { Dialog } from '@angular/cdk/dialog';
import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NiimbotPrinterService } from '@nebalkon-workspace/niimbot-printer';
import {
  CellOccupantOwner,
  WarehouseCellContentsResponse,
  WarehouseService,
} from '@nebalkon-workspace/storage-api';
import {
  NotButton,
  NotSystemNotificationService,
} from '@nebalkon-workspace/ui-kit';
import { formatWarehouseCellLabel } from './staff-warehouse-cell-picker.util';
import { printStaffCellQr } from '../utils/staff-entity-qr-print.util';
import { warehouseRouteSegment } from '../utils/warehouse-route.util';

@Component({
  selector: 'lib-staff-warehouse-cell-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, RouterLink, NotButton],
  templateUrl: './staff-warehouse-cell-detail.component.html',
  styleUrl: './staff-warehouse-cell-detail.component.scss',
})
export class StaffWarehouseCellDetailComponent implements OnInit {
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #warehouseService = inject(WarehouseService);
  readonly #translate = inject(TranslateService);
  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #notification = inject(NotSystemNotificationService);
  readonly #printer = inject(NiimbotPrinterService);
  readonly #dialog = inject(Dialog);

  protected readonly contents = signal<WarehouseCellContentsResponse | null>(
    null,
  );
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly printingQr = signal(false);

  protected readonly isEmpty = computed(() => {
    const data = this.contents();
    if (!data) {
      return false;
    }
    return data.boxes.length === 0 && data.standalone_items.length === 0;
  });

  protected readonly cellAddressLabel = computed(() => {
    const data = this.contents();
    if (!data) {
      return '';
    }
    return data.cell.address || formatWarehouseCellLabel(data.cell);
  });

  protected readonly warehouseBackLink = computed(() => {
    const data = this.contents();
    if (!data) {
      return null;
    }
    const segment = warehouseRouteSegment({
      id: data.warehouse.id,
      slug: data.warehouse.slug,
    });
    return [this.#cabinetPaths.staff, 'warehouses', segment] as const;
  });

  public ngOnInit(): void {
    const cellId = this.#route.snapshot.paramMap.get('cellId');
    if (!cellId) {
      this.error.set(
        this.#translate.instant('admin.warehouseCell.notFound'),
      );
      this.loading.set(false);
      return;
    }

    this.#loadContents(cellId);
  }

  #loadContents(cellId: string): void {
    this.loading.set(true);
    this.error.set(null);

    this.#warehouseService.getWarehouseCellContents(cellId).subscribe({
      next: (response) => {
        this.contents.set(response);
        this.#canonicalizeRoute(response);
        this.loading.set(false);
      },
      error: (err) => {
        console.error('Error loading warehouse cell contents:', err);
        this.error.set(
          this.#translate.instant('admin.warehouseCell.loadError'),
        );
        this.loading.set(false);
      },
    });
  }

  #canonicalizeRoute(response: WarehouseCellContentsResponse): void {
    const segment = warehouseRouteSegment({
      id: response.warehouse.id,
      slug: response.warehouse.slug,
    });
    const routeSlug = this.#route.snapshot.paramMap.get('slug');
    const cellId = response.cell.id;

    if (routeSlug === segment) {
      return;
    }

    void this.#router.navigate(
      [this.#cabinetPaths.staff, 'warehouses', segment, 'cells', cellId],
      { replaceUrl: true },
    );
  }

  protected storageStatusLabel(status: string): string {
    const keyMap: Record<string, string> = {
      at_client: 'admin.storageRequests.storageStatusAtClient',
      pending: 'admin.storageRequests.storageStatusPending',
      mover_accepted: 'admin.storageRequests.storageStatusMoverAccepted',
      in_transit_to_warehouse:
        'admin.storageRequests.storageStatusInTransitWarehouse',
      stored: 'admin.storageRequests.storageStatusStored',
      in_transit_to_client:
        'admin.storageRequests.storageStatusInTransitClient',
      delivered: 'admin.storageRequests.storageStatusDelivered',
    };
    const key = keyMap[status];
    return key ? this.#translate.instant(key) : status;
  }

  protected ownerDisplayName(owner: CellOccupantOwner): string {
    const parts = [owner.last_name, owner.first_name]
      .map((value) => value?.trim())
      .filter(Boolean);
    const name = parts.join(' ').trim();
    if (name) {
      return name;
    }
    return this.formatPhone(owner.phone);
  }

  protected ownerContactLine(owner: CellOccupantOwner): string {
    const name = this.ownerDisplayName(owner);
    const phone = this.formatPhone(owner.phone);
    if (name && phone && name !== phone) {
      return `${name} · ${phone}`;
    }
    return name || phone || '—';
  }

  protected formatPhone(phone: string): string {
    if (!phone) {
      return '';
    }
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('375')) {
      return `+${cleaned}`;
    }
    if (cleaned.length === 9) {
      return `+375${cleaned}`;
    }
    return phone.startsWith('+') ? phone : `+${cleaned}`;
  }

  protected ownerProfileLink(userId: string): string[] {
    return [this.#cabinetPaths.staff, 'users', userId];
  }

  protected async onPrintQr(): Promise<void> {
    const cellId = this.contents()?.cell.id;
    if (!cellId || this.printingQr()) {
      return;
    }

    this.printingQr.set(true);
    try {
      await printStaffCellQr({
        entityId: cellId,
        entityKind: 'cell',
        caption: this.cellAddressLabel(),
        printer: this.#printer,
        translate: this.#translate,
        notification: this.#notification,
        dialog: this.#dialog,
      });
    } finally {
      this.printingQr.set(false);
    }
  }
}
